import {
  Link,
  Text,
} from '@chakra-ui/react';
import {
  useHeavyDsi,
} from '@src/modules/performance/context/reports-api-hooks';
import { selectedDatabaseIdState, currentDatabaseSelector } from '@transport-insights/uikit';
import { useRecoilValue } from 'recoil';
import LineRangeAreaChart from '@src/modules/performance/components/charts/LineRangeAreaChart';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import { filterChartDataByName } from '@src/modules/performance/shared/helpers';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Crash Factors (per 100,000 Population)',
  content: [
    {
      title: 'DSI involving Heavy vehicles ',
      description:
  <Text fontSize="inherit" mb={2}>
    The total number of deaths and serious injuries (DSI) each year in the selected network area per 100,000 of population that occurred in crashes involving heavy vehicles (including trucks, buses, and school buses).
  </Text>,
      source:
  <Text fontSize="inherit" as="span">
    Crash data sourced from
    {' '}
    <Link href="https://www.nzta.govt.nz/partners/data-and-tools/crash-analysis-system" isExternal>NZTA Crash Analysis System.</Link>
    {' '}
    Population sourced from Statistics New Zealand
    {' '}
    <Link href="https://www.stats.govt.nz/information-releases/subnational-population-estimates-at-30-june-2023/" isExternal>Subnational Population Estimates.</Link>
    {' '}
    Road Efficiency Group Te Ringa Maimoa has calculated these results from these data sources.
  </Text>,
    },
  ],
};

function RoadSafetyCrashFactors() {
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const currentDatabase = useRecoilValue(currentDatabaseSelector);
  const { data: heavyDsiData, isLoading } = useHeavyDsi();
  const isRegionNationalOrStateHighways = rcaId >= 1000 || rcaId === 81;
  const isNational = rcaId === 1000;
  let chartFilters = [];
  if (isNational) {
    chartFilters = ['Peer', 'Percentile'];
  } else if (isRegionNationalOrStateHighways) {
    chartFilters = ['Peer'];
  }

  // Heavy Dsi Chart Data
  const heavyDsiYears = [...new Set(heavyDsiData?.map((item) => item.Year))];
  const heavyDsiChartData = [{
    name: currentDatabase?.name,
    type: 'line',
    data: heavyDsiData?.map((item) => ({ x: item.Year, y: item.Value })),
    color: CHART_COLOURS.blue,
  }, {
    name: 'Peer Group',
    type: 'line',
    data: heavyDsiData?.map((item) => ({ x: item.Year, y: item.PeerGroupValue })),
    color: CHART_COLOURS.black,
  }, {
    name: '25-75th National Percentile',
    type: 'rangeArea',
    data: heavyDsiData?.map((item) => ({ x: item.Year, y: [item.LowPercentile, item.HighPercentile] })),
    color: CHART_COLOURS.lightgrey,
  }];

  // Filter out the Peer Group and Percentile data if it's a national report, and Peer Group if it's a region or state highways report
  const heavyDsiChartSeries = filterChartDataByName(
    heavyDsiChartData,
    chartFilters,
  );

  const markers = isRegionNationalOrStateHighways ? {
    size: [3, 0],
    hover: {
      size: 6,
    },
  } : {
    size: [3, 3, 0],
    hover: {
      size: 6,
    },
  };

  const stroke = isRegionNationalOrStateHighways ? {
    curve: 'straight',
    width: [2, 0],
  } : {
    curve: 'straight',
    width: [2, 2, 0],
  };

  return (
    <ReportSection
      title="Crash Factors (per 100,000 Population)"
      informationSource={informationSource}
      isLoading={isLoading || !heavyDsiData}
      lockYear={heavyDsiData?.[0]?.LockYear}
    >
      <LineRangeAreaChart
        title="DSI involving Heavy vehicles "
        categories={heavyDsiYears}
        series={heavyDsiChartSeries}
        width="240"
        height="280"
        stroke={stroke}
        markers={markers}
        prefix=""
        suffix=""
        decimalPoints={1}
      />
    </ReportSection>
  );
}

export default RoadSafetyCrashFactors;
